import React, { Component, useState, useEffect } from 'react';
import { Globals } from './Globals.js';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import { CLOCKWISE, COUNTER_CLOCKWISE } from './Rhombus.js';


const ControlButton = ({ name, enabled, handler}) => {
  return <Button variant="outlined" onClick={()=>{handler(name);}} disabled={!enabled}>{name}</Button>;
};

const ControlButtonGroup = () => {
  const [drawEnabled, setDrawEnabled] = useState(true);
  const [stopEnabled, setStopEnabled] = useState(false);
  const [resetEnabled, setResetEnabled] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);

  const handleCpStateChange = (cp_state) => {
    if (cp_state == Globals.CP_STOP) {
      setDrawEnabled(true);
      setStopEnabled(false);
      setResetEnabled(true);
    }
  }

  useEffect( () => {
    if (!isRegistered) {
      let globals = new Globals();
      globals.register_cp_state_listener( handleCpStateChange);
      setIsRegistered(true);
    }
  });

  const handleClick = (name) => {
    let globals = new Globals();
    switch (name) {
      case 'Draw':
        setDrawEnabled(false);
        setStopEnabled(true);
        setResetEnabled(true);
        globals.cp_state = Globals.CP_DRAW;
        globals.status_message = Globals.SM_DRAWING;
        break;
      case 'Stop':
        setDrawEnabled(true);
        setStopEnabled(false);
        setResetEnabled(true);
        globals.cp_state = Globals.CP_STOP;
        globals.status_message = Globals.SM_MANUALLY_STOPPED;
        break;
      case 'Reset':
        setDrawEnabled(true);
        setStopEnabled(false);
        setResetEnabled(false);
        globals.cp_state = Globals.CP_RESET;
        globals.status_message = Globals.SM_INITIAL_STATE;
        break;
      default:
        throw `unhandled case:${name}`;
    }
  };

  return (
    <ButtonGroup>
      <ControlButton name='Draw' handler={handleClick} enabled={drawEnabled}/>
      <ControlButton name='Stop' handler={handleClick} enabled={stopEnabled}/>
      <ControlButton name='Reset' handler={handleClick} enabled={resetEnabled}/>
    </ButtonGroup>
  );
};

const FillDirectionControl = () => {
  const DIRECTION_ALGORITHMS = [0,1];
  const DIRECTION_ALGORITHM_LABELS = ['Clockwise', 'Counter-Clockwise'];
  const DIRECTION_ENUMS = [CLOCKWISE, COUNTER_CLOCKWISE];
  const [directionAlgorithm, setDirectionAlgorithm] = useState(0);

  return (
    <Box sx={{ border: 1, m: 1, p: 1, borderColor: 'grey.500' }}>
      <p>Next Grow Point Selection</p>
      <InputLabel id="next-grow-point-select-label">Algorithm</InputLabel>
      <Select 
        labelId="next-grow-point-select-label"
        value={directionAlgorithm}
        onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
          setDirectionAlgorithm(event.target.value);
          let globals = new Globals();
          globals.direction = DIRECTION_ENUMS[event.target.value];
        }}
      >
        {DIRECTION_ALGORITHMS.map((dirAlg) => (
          <MenuItem key={dirAlg} value={dirAlg}>
            {DIRECTION_ALGORITHM_LABELS[dirAlg]}
          </MenuItem>
        ))};
      </Select>
    </Box>
  );
}

const NextNodePickingControl = () => {
  const NEXT_NODE_PICKS = [0,1,2];
  const NEXT_NODE_PICK_LABELS = ['Closest To Center', 'Random with Interior Angles', 'Random'];
  const NEXT_NODE_PICK_ENUMS = [Globals.NN_CLOSEST_TO_CENTER, Globals.NN_RANDOM_INTERIOR, Globals.NN_RANDOM];
  const [nextNodeCriteria, setNextNodeCriteria] = useState(0);

  return (
    <Box sx={{ border: 1, m: 1, p: 1, borderColor: 'grey.500' }}>
      <p>Next Node Picking Criteria</p>
      <InputLabel id="next-node-pick-label">Algorithm</InputLabel>
      <Select 
        labelId="next-node-pick-label"
        value={nextNodeCriteria}
        onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
          setNextNodeCriteria(event.target.value);
          let globals = new Globals();
          globals.next_node_criteria = NEXT_NODE_PICK_ENUMS[event.target.value];
        }}
      >
        {NEXT_NODE_PICKS.map((nextNodeCrit) => (
          <MenuItem key={nextNodeCrit} value={nextNodeCrit}>
            {NEXT_NODE_PICK_LABELS[nextNodeCrit]}
          </MenuItem>
        ))};
      </Select>
    </Box>
  );
}

const StatusMessage = () => {
  const [statusMessage, setStatusMessage] = useState('');
  const [isRegistered, setIsRegistered] = useState(false);

  const ENUM_TO_TEXT = {};
  ENUM_TO_TEXT[Globals.SM_INITIAL_STATE] = '';
  ENUM_TO_TEXT[Globals.SM_DRAWING] = 'Drawing...';
  ENUM_TO_TEXT[Globals.SM_MANUALLY_STOPPED] = 'Stopped';
  ENUM_TO_TEXT[Globals.SM_FINISHED] = 'Finished. View is full.';
  ENUM_TO_TEXT[Globals.SM_STOPPED_DUE_TO_OVERLAP] = 'Stopped because of rhombus overlap.';
  ENUM_TO_TEXT[Globals.SM_STOPPED_DUE_TO_HOLE] = 'Stopped because the vertex selected will create a hole.';
  ENUM_TO_TEXT[Globals.SM_INTERNAL_ERROR] = 'Stopped due to internal error.';

  const SM_TO_COLOR = {}; 
  SM_TO_COLOR[Globals.SM_INITIAL_STATE] = 'info.main';
  SM_TO_COLOR[Globals.SM_DRAWING] = 'info.main';
  SM_TO_COLOR[Globals.SM_MANUALLY_STOPPED] = 'warning.main';
  SM_TO_COLOR[Globals.SM_FINISHED] = 'success.main';
  SM_TO_COLOR[Globals.SM_STOPPED_DUE_TO_OVERLAP] = 'warning.main';
  SM_TO_COLOR[Globals.SM_STOPPED_DUE_TO_HOLE] = 'warning.main';
  SM_TO_COLOR[Globals.SM_INTERNAL_ERROR] = 'error.main';

  const handleMessageChange = (message) => {
   setStatusMessage(message);
  }

  useEffect( () => {
    if (!isRegistered) {
      let globals = new Globals();
      globals.register_status_message_listener( handleMessageChange);
      setIsRegistered(true);
    }
  });

  return (
    <Box sx={{ overflow:'auto', border: 1, m: 1, p: 1, color:SM_TO_COLOR[statusMessage]}}>
      {ENUM_TO_TEXT[statusMessage]}
    </Box>
  );
}

class ControlPanel extends Component {
  render() {
    return (
      <Stack spacing={1} ml={1}>
        <FillDirectionControl/>
        <NextNodePickingControl/>
        <StatusMessage/>
        <ControlButtonGroup/>
      </Stack>
    );
  }
}


export default ControlPanel;
